import { Box } from '@mui/material';
import React from 'react';

import { DATA_TEST } from '../../enums';

import { NAV_COLOR_PALETTE } from '../../Components/styleConstants';
import { UtilityNames, UtilityTrayMenuItem, UtilityTrayMenuSection } from '../../types';
import { useUtilityTrayContext } from '../Context/UtilityTrayContext';

import { MobileUtilityMainMenu } from './MobileUtilityMainMenu';
import { MobileUtilitySubmenu } from './MobileUtilitySubmenu';

interface IMobileUtilityTrayProps {
  firstTrayMenuItemRef?: React.MutableRefObject<HTMLLIElement>;
  closeUtilityMenu(): void;
}
export const MobileUtilityTray: React.FC<IMobileUtilityTrayProps> = ({ closeUtilityMenu, firstTrayMenuItemRef }) => {
  const {
    helpOptions,
    reportsOptions,
    notificationsOptions,
    settingsOptions,
    customUtilityOptions,
    profileSettingsOptions
  } = useUtilityTrayContext();
  const [activeSubmenu, setActiveSubmenu] = React.useState<UtilityNames | null>(null);
  const [activeSubmenuOptions, setActiveSubmenuOptions] = React.useState<UtilityTrayMenuItem[] | null>(null);

  React.useEffect(() => {
    // Flatten menu sections to a single array of menu items
    // This is a temporary solution until the mobile utility tray is
    // refactored to display menu sections to match the desktop utility tray
    const flattenMenuSections = (menuSections: UtilityTrayMenuSection[]) =>
      menuSections?.reduce((acc, section) => [...acc, ...section.menuItems], []);

    if (activeSubmenu) {
      switch (activeSubmenu) {
        case UtilityNames.help:
          setActiveSubmenuOptions(flattenMenuSections(helpOptions?.menuSections || []));
          break;
        case UtilityNames.reports:
          setActiveSubmenuOptions(flattenMenuSections(reportsOptions?.menuSections || []));
          break;
        case UtilityNames.notifications:
          setActiveSubmenuOptions(flattenMenuSections(notificationsOptions?.menuSections || []));
          break;
        case UtilityNames.settings:
          setActiveSubmenuOptions(flattenMenuSections(settingsOptions?.menuSections || []));
          break;
        case UtilityNames.custom:
          setActiveSubmenuOptions(flattenMenuSections(customUtilityOptions?.menuSections || []));
          break;
        default:
          setActiveSubmenuOptions(null);
      }
    }
  }, [activeSubmenu]);

  return (
    <Box
      data-test={DATA_TEST.mobile_utility_tray}
      sx={{
        '.MuiMenuItem-root': {
          minHeight: 40
        },
        '.Mui-focusVisible': {
          outline: `2px solid ${NAV_COLOR_PALETTE.focus}`
        }
      }}
    >
      {!activeSubmenu && (
        <MobileUtilityMainMenu
          helpOptions={helpOptions}
          reportsOptions={reportsOptions}
          notificationsOptions={notificationsOptions}
          customUtilityOptions={customUtilityOptions}
          settingsOptions={settingsOptions}
          profileSettingsOptions={profileSettingsOptions}
          setActiveSubmenu={setActiveSubmenu}
          closeUtilityMenu={closeUtilityMenu}
          firstTrayMenuItemRef={firstTrayMenuItemRef}
        />
      )}
      {activeSubmenu && activeSubmenuOptions && (
        <MobileUtilitySubmenu
          optionLabel={
            activeSubmenu === UtilityNames.custom ? customUtilityOptions?.utilityName ?? activeSubmenu : activeSubmenu
          }
          options={activeSubmenuOptions}
          showMainMenu={() => setActiveSubmenu(null)}
          closeUtilityMenu={closeUtilityMenu}
          firstTrayMenuItemRef={firstTrayMenuItemRef}
        />
      )}
    </Box>
  );
};
